import { useEffect, useMemo, useRef, useState } from "react";
import { useStateValue } from "../../context/StateProvider";
import {
  ChevronDown,
  Crown,
  Key,
  MoreVertical,
  Power,
  PowerOff,
  Star,
  Trash2,
  UserRoundPen,
  UsersRound,
} from "lucide-react";
import DeleteDisable from "./delete-disable-user";
import { useNavigate } from "react-router-dom";
import AccessRole from "./access-role";
import {
  getAllusers,
  hardDeleteUser,
  updateUserProfile,
} from "../../utils/firebasefunctions";
import { actionType } from "../../context/reducer";
import { toast } from "react-toastify";
import { isSuperAdminOwner } from "../../utils/brokerageSettings";

// People are grouped under these headings, in this order. Each role gets a
// tint used for the avatar + the count badge.
const ROLE_GROUPS = [
  {
    role: "Admin",
    label: "Team leads",
    tint: "bg-violet-100 text-violet-700",
  },
  {
    role: "Employee",
    label: "Agents",
    tint: "bg-blue-100 text-blue-700",
  },
  {
    role: "Supervisor",
    label: "Client care",
    tint: "bg-emerald-100 text-emerald-700",
  },
  {
    role: "Accountant",
    label: "Accountants",
    tint: "bg-amber-100 text-amber-700",
  },
];

function MainUserManagement() {
  const [{ users, user }, dispatch] = useStateValue();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [mood, setMood] = useState("");
  const [userId, setUserId] = useState("");
  const [isAccess, setIsAccess] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const navigate = useNavigate();
  const cleanedRef = useRef(false);

  // One-time cleanup: hard-delete user docs with no name and no email.
  // Runs once per mount, only if there's something to clean.
  useEffect(() => {
    if (cleanedRef.current) return;
    // Only the owner runs this destructive cleanup — a regular Team Lead or
    // Client Care opening this page must not silently hard-delete records.
    if (!isSuperAdminOwner(user)) return;
    if (!Array.isArray(users) || users.length === 0) return;
    const blanks = users.filter((u) => {
      const f = (u?.firstname || "").trim();
      const l = (u?.lastname || "").trim();
      const e = (u?.email || "").trim();
      return !f && !l && !e;
    });
    if (blanks.length === 0) return;
    cleanedRef.current = true;
    (async () => {
      let removed = 0;
      for (const blank of blanks) {
        try {
          await hardDeleteUser(blank.id);
          removed += 1;
        } catch (err) {
          console.error("Failed to delete blank user", blank.id, err);
        }
      }
      if (removed > 0) {
        try {
          const response = await getAllusers("users");
          dispatch({
            type: actionType.STORE_ALLUSERS,
            teamleads: response.filter((d) => d.role === "Admin"),
            clients: response.filter((d) => d.role === "Supervisor"),
            employees: response,
            allusers: response,
          });
        } catch (err) {
          console.error("Failed to refresh users:", err);
        }
      }
    })();
  }, [users, dispatch]);

  const handleDelete = (id) => {
    setMood("delete");
    setUserId(id);
    setIsOpen(true);
  };

  const handleDisable = (id) => {
    setMood("disabled");
    setUserId(id);
    setIsOpen(true);
  };

  const handleranking = async (item) => {
    try {
      await updateUserProfile(item.id, { rankingaccess: !item.rankingaccess });
      getallusers();
    } catch (err) {}
  };

  const handleActiveTeamLead = async (item) => {
    // Missing field counts as ON, so the first toggle turns it OFF.
    const next = item.activeteamlead === false ? true : false;
    try {
      await updateUserProfile(item.id, { activeteamlead: next });
      getallusers();
      toast.success(next ? "Team lead turned on" : "Team lead turned off");
    } catch (err) {
      toast.error("Could not update team lead status");
    }
  };

  const handleAccess = (id) => {
    setUserId(id);
    setIsAccess(true);
  };

  // Owner-only "two hats" switch: an Agent who also does Client Care duties
  // (file queue, team contacts, texting). Never grants commission visibility
  // or turn-in approvals — see actsAsClientCare in utils/roles.js.
  const handleClientCareDuties = async (item) => {
    const next = item.isClientCare === true ? false : true;
    try {
      await updateUserProfile(item.id, { isClientCare: next });
      getallusers();
      toast.success(
        next ? "Client care duties turned on" : "Client care duties turned off",
      );
    } catch (err) {
      toast.error("Could not update client care duties");
    }
  };

  const filteredUsers = useMemo(() => {
    const text = search.toLowerCase().trim();

    return users
      .filter((item) => {
        // ❌ Deleted users skip
        if (item.type === 1) return false;

        // ❌ Hide blank rows (no name + no email) — stale/garbage records
        const hasIdentity =
          (item.firstname || "").trim() ||
          (item.lastname || "").trim() ||
          (item.email || "").trim();
        if (!hasIdentity) return false;

        // ✅ If current user is Supervisor → only show Employees
        if (user?.role === "Supervisor") {
          return item.role === "Employee";
        }

        return true;
      })
      .filter((items) => {
        if (!text) return true;

        const fullName = `${items.firstname || ""} ${items.lastname || ""}`
          .toLowerCase()
          .trim();

        const email = items.email?.toLowerCase() || "";
        const phone = items.phonenumber?.toString().toLowerCase() || "";

        return (
          fullName.includes(text) ||
          email.includes(text) ||
          phone.includes(text)
        );
      });
  }, [users, search, user]);

  const groupedUsers = useMemo(() => {
    const g = { Admin: [], Employee: [], Supervisor: [], Accountant: [] };
    filteredUsers.forEach((u) => {
      if (u.role === "Admin") g.Admin.push(u);
      else if (u.role === "Employee") g.Employee.push(u);
      else if (u.role === "Accountant") g.Accountant.push(u);
      else g.Supervisor.push(u); // Supervisor + any unknown role → Client care
    });
    // Active team leads first, switched-off ones at the bottom.
    g.Admin = [...g.Admin].sort(
      (a, b) =>
        (a.activeteamlead === false ? 1 : 0) -
        (b.activeteamlead === false ? 1 : 0),
    );
    return g;
  }, [filteredUsers]);

  async function getallusers() {
    await getAllusers("users")
      .then(async (response) => {
        dispatch({
          type: actionType.STORE_ALLUSERS,
          teamleads: response.filter((doc) => doc.role === "Admin"),
          clients: response.filter((doc) => doc.role === "Supervisor"),
          employees: response,
          allusers: response,
        });
      })
      .catch((err) => {
        toast.error(String(err?.message || err));
      });
  }

  return (
    <div className="">
      {openMenuId && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setOpenMenuId(null)}
        />
      )}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        {/* Header: title + search + create */}
        <div className="p-5 flex flex-col md:flex-row justify-between md:items-center gap-3 border-b border-gray-100">
          <h2 className="text-xl font-semibold">Users</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative flex-1 sm:w-72">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.8}
                  stroke="currentColor"
                  className="size-[18px]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search people"
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#1E293B] text-sm"
              />
            </div>
            <button
              onClick={() =>
                navigate("/dashboard/create-user?page=usermanagment")
              }
              className="inline-flex items-center justify-center gap-2 px-4 py-2 font-medium text-white text-sm rounded-lg bg-[#1E293B] hover:bg-[#0f172a] transition-colors whitespace-nowrap"
            >
              <UsersRound className="h-4 w-4" />
              Create user
            </button>
          </div>
        </div>

        {/* Grouped people list */}
        {filteredUsers.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500 text-sm">
            No users found
          </div>
        ) : (
          ROLE_GROUPS.map((grp) => {
            const rows = groupedUsers[grp.role] || [];
            if (rows.length === 0) return null;
            const collapsed = collapsedGroups[grp.role];
            return (
              <div key={grp.role}>
                <button
                  type="button"
                  onClick={() =>
                    setCollapsedGroups((c) => ({
                      ...c,
                      [grp.role]: !c[grp.role],
                    }))
                  }
                  className="w-full flex items-center gap-2 px-5 py-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 transition-transform ${
                      collapsed ? "-rotate-90" : ""
                    }`}
                  />
                  <span className="font-medium text-sm text-gray-800">
                    {grp.label}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${grp.tint}`}>
                    {rows.length}
                  </span>
                </button>

                {!collapsed &&
                  rows.map((item) => {
                    const gateEdit =
                      item.role != "Admin" ||
                      (item.id !== user.id && isSuperAdminOwner(user));
                    // Disable/Delete are stricter than Edit: only the owner or a
                    // Team Lead (Admin) may remove an account. Client Care
                    // (Supervisor) may edit details but not disable/delete.
                    const gateManage =
                      gateEdit &&
                      (isSuperAdminOwner(user) || user?.role === "Admin");
                    const canToggleTeamLead =
                      item.role === "Admin" &&
                      item.id !== user.id &&
                      isSuperAdminOwner(user) &&
                      !item.isDemo;
                    const canGrantPrivileges =
                      isSuperAdminOwner(user) && item.role === "Supervisor";
                    const canRank =
                      item.role != "Supervisor" && item.role != "Accountant";
                    const off = item.activeteamlead === false;

                    const menu = [];
                    if (gateEdit && item.type !== 2)
                      menu.push({
                        Icon: UserRoundPen,
                        label: "Edit details",
                        onClick: () =>
                          navigate(
                            `/dashboard/update-user/${item.id}?page=usermanagment`,
                          ),
                      });
                    if (canToggleTeamLead)
                      menu.push({
                        Icon: Crown,
                        label: off
                          ? "Turn on as team lead"
                          : "Turn off as team lead",
                        onClick: () => handleActiveTeamLead(item),
                      });
                    if (canGrantPrivileges)
                      menu.push({
                        Icon: Key,
                        label: "Client care privileges",
                        onClick: () => handleAccess(item),
                      });
                    if (
                      isSuperAdminOwner(user) &&
                      item.role === "Employee" &&
                      !item.isDemo
                    )
                      menu.push({
                        Icon: Key,
                        label:
                          item.isClientCare === true
                            ? "Turn off client care duties"
                            : "Turn on client care duties",
                        onClick: () => handleClientCareDuties(item),
                      });
                    if (canRank)
                      menu.push({
                        Icon: Star,
                        label: item.rankingaccess
                          ? "Remove from rankings"
                          : "Add to rankings",
                        onClick: () => handleranking(item),
                      });
                    if (gateManage)
                      menu.push({
                        Icon: item.type === 2 ? Power : PowerOff,
                        label:
                          item.type === 2
                            ? "Enable account"
                            : "Disable account",
                        onClick: () => handleDisable(item.id),
                      });
                    if (gateManage)
                      menu.push({
                        Icon: Trash2,
                        label: "Delete",
                        danger: true,
                        onClick: () => handleDelete(item.id),
                      });

                    const initials =
                      `${(item.firstname || "")[0] || ""}${
                        (item.lastname || "")[0] || ""
                      }`.toUpperCase() || "?";

                    return (
                      <div
                        key={item.id}
                        className="relative flex items-center gap-3 px-5 py-2.5 border-t border-gray-100 hover:bg-gray-50/70 transition-colors"
                      >
                        {item?.profilepicture ? (
                          <img
                            src={item.profilepicture}
                            alt=""
                            className="size-9 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div
                            className={`size-9 rounded-full flex items-center justify-center text-[13px] font-medium flex-shrink-0 ${grp.tint}`}
                          >
                            {initials}
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">
                            {item.firstname} {item.lastname}
                            {item.role === "Employee" &&
                              item.isClientCare === true && (
                                <span className="ml-2 align-middle text-[10px] font-bold bg-slate-100 text-linkColor rounded-full px-2 py-0.5">
                                  + Client Care
                                </span>
                              )}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {item.email}
                            {item.phonenumber ? ` · ${item.phonenumber}` : ""}
                          </div>
                        </div>

                        {item.type === 2 ? (
                          <span className="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 flex-shrink-0">
                            Disabled
                          </span>
                        ) : item.role === "Admin" && off ? (
                          // Only the exception gets a badge. A green "Bookable"
                          // pill sat on nearly every team lead, which made it
                          // decoration rather than information. "Off" is the
                          // state actually worth spotting in a long list.
                          <span className="text-xs px-2.5 py-1 rounded-full flex-shrink-0 bg-gray-100 text-gray-500">
                            Off
                          </span>
                        ) : null}

                        {menu.length > 0 && (
                          <button
                            type="button"
                            aria-label="Actions"
                            onClick={() =>
                              setOpenMenuId(
                                openMenuId === item.id ? null : item.id,
                              )
                            }
                            className="size-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 flex-shrink-0"
                          >
                            <MoreVertical size={18} />
                          </button>
                        )}

                        {openMenuId === item.id && (
                          <div className="absolute right-4 top-12 z-20 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5">
                            {menu.map((mi, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                  mi.onClick();
                                  setOpenMenuId(null);
                                }}
                                className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
                                  mi.danger ? "text-red-600" : "text-gray-700"
                                }`}
                              >
                                <mi.Icon
                                  size={17}
                                  className={
                                    mi.danger ? "text-red-500" : "text-gray-400"
                                  }
                                />
                                {mi.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>

      {isOpen && (
        <DeleteDisable
          userId={userId}
          mood={mood}
          onClose={() => setIsOpen(false)}
        />
      )}
      {isAccess && (
        <AccessRole onClose={() => setIsAccess(false)} userData={userId} />
      )}
    </div>
  );
}

export default MainUserManagement;
